import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, Eye, HelpCircle, Info, MousePointerClick } from "lucide-react";
import { GUIDE } from "@/lib/guide";

/** Tooltip di spiegazione accanto a un'etichetta di campo. */
export const Hint = ({ testo, id }) => {
  const [aperto, setAperto] = useState(false);
  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label="Spiegazione"
        data-testid={id ? `hint-${id}` : undefined}
        onClick={(e) => {
          e.preventDefault();
          setAperto((v) => !v);
        }}
        onMouseEnter={() => setAperto(true)}
        onMouseLeave={() => setAperto(false)}
        className="ml-1 text-slate-300 transition-colors hover:text-slate-900"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {aperto && (
        <span
          role="tooltip"
          data-testid={id ? `tooltip-${id}` : undefined}
          className="absolute bottom-full left-1/2 z-[9996] mb-2 w-60 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-normal normal-case leading-relaxed tracking-normal text-white shadow-xl"
        >
          {testo}
        </span>
      )}
    </span>
  );
};

/** Card di guida mostrata in cima a ogni pagina, apribile e chiudibile. */
export const GuidaPagina = () => {
  const { pathname } = useLocation();
  const guida = GUIDE[pathname];
  const [aperta, setAperta] = useState(() => localStorage.getItem("rg_guida_chiusa") !== "1");

  if (!guida) return null;

  const toggle = () => {
    const nuovo = !aperta;
    setAperta(nuovo);
    localStorage.setItem("rg_guida_chiusa", nuovo ? "0" : "1");
  };

  return (
    <section className="mb-6 overflow-hidden rounded-2xl border border-sky-200 bg-sky-50/60" data-testid="guida-pagina">
      <button
        onClick={toggle}
        data-testid="guida-toggle"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-sky-100/60 sm:px-5"
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <Info className="h-4 w-4 shrink-0 text-sky-600" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-900">{guida.titolo}</span>
            {!aperta && (
              <span className="mt-0.5 block truncate text-xs text-slate-500">{guida.testo}</span>
            )}
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-sky-600 transition-transform ${aperta ? "rotate-180" : ""}`}
        />
      </button>

      {aperta && (
        <div className="border-t border-sky-200/70 px-4 pb-5 pt-4 sm:px-5" data-testid="guida-contenuto">
          <p className="max-w-3xl text-sm leading-relaxed text-slate-600">{guida.testo}</p>

          <div className="mt-4 grid gap-5 md:grid-cols-2">
            <div>
              <p className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-700">
                <Eye className="h-3.5 w-3.5" /> Cosa vedi
              </p>
              <ul className="mt-2 space-y-1.5">
                {guida.vedi.map((v) => (
                  <li key={v} className="flex gap-2 text-xs leading-relaxed text-slate-600">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-sky-400" />
                    {v}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
                <MousePointerClick className="h-3.5 w-3.5" /> Cosa puoi fare
              </p>
              <ul className="mt-2 space-y-1.5">
                {guida.fai.map((v) => (
                  <li key={v} className="flex gap-2 text-xs leading-relaxed text-slate-600">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-400" />
                    {v}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-4 text-[11px] text-slate-400">
            Serve altro? Chiedi al robot PM-Bot in basso a destra: risponde a voce e con i sottotitoli.
          </p>
        </div>
      )}
    </section>
  );
};

export default GuidaPagina;
